/**
 * Hours Spotlight — editorial pull-quote with avg hours + quality/consistency
 * triplet AND marginalia whispers for best/worst night.
 *
 * Registers as <aramgah-hours-spotlight>.
 */
import { store } from '../store.ts';
import { formatHours } from '../types.ts';
import type { SleepSummary } from '../types.ts';

function ddmm(date: string): string {
  const [y, m, d] = date.split('-');
  if (!y || !m || !d) return date;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1] ?? ''}`;
}

export class AramgahHoursSpotlight extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(s: SleepSummary): void {
    const best = s.insights.best_night;
    const worst = s.insights.worst_night;

    this.#shadow.innerHTML = /* html */ `
<style>
  :host {
    display: block;
    container-type: inline-size;
    position: relative;
    border: 1px solid var(--rule);
    border-radius: 12px;
    padding: 28px 24px 30px;
    background:
      linear-gradient(155deg, oklch(22% 0.06 270 / 0.55) 0%, transparent 60%),
      var(--paper-card);
    overflow: hidden;
    isolation: isolate;
  }
  :host::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 60%;
    height: 60%;
    background: radial-gradient(circle, oklch(91% 0.04 80 / 0.06), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
  .label {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--moon-cream);
    position: relative;
    z-index: 1;
  }
  .main {
    font-family: var(--display);
    font-size: clamp(56px, 17cqw, 96px);
    font-weight: 700;
    margin-top: 10px;
    line-height: 0.95;
    letter-spacing: -0.026em;
    color: var(--moon-cream);
    font-feature-settings: 'tnum' 1, 'ss01' 1, 'cv11' 1, 'lnum' 1;
    text-wrap: balance;
    text-shadow: 0 0 22px oklch(91% 0.04 80 / 0.18);
    position: relative;
    z-index: 1;
  }
  .cap {
    font-family: var(--display);
    font-style: italic;
    font-size: 22px;
    color: var(--ink-dim);
    margin-top: 8px;
    text-wrap: pretty;
    position: relative;
    z-index: 1;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px dashed var(--rule);
    position: relative;
    z-index: 1;
  }
  @container (max-width: 420px) {
    .grid { grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .cell .v { font-size: 22px; }
  }
  .cell { display: grid; gap: 4px; }
  .cell .v {
    font-family: var(--display);
    font-size: 26px;
    font-weight: 600;
    color: var(--ink);
    font-feature-settings: 'tnum' 1, 'ss01' 1;
    letter-spacing: -0.01em;
  }
  .cell .k {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }

  /* Marginalia whispers — best & worst night, italic Cormorant */
  .whispers {
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid var(--rule);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 22px;
    position: relative;
    z-index: 1;
  }
  @container (max-width: 420px) {
    .whispers { grid-template-columns: 1fr; gap: 14px; }
  }
  .whisper .glyph {
    font-family: var(--display);
    font-style: italic;
    font-size: 32px;
    line-height: 1;
    color: var(--moon);
    opacity: 0.4;
  }
  .whisper.right .glyph {
    text-align: right;
    color: var(--rose);
  }
  .whisper .quote {
    font-family: var(--display);
    font-style: italic;
    font-size: 17px;
    color: var(--ink-dim);
    line-height: 1.35;
    margin-top: 6px;
    text-wrap: pretty;
  }
  .whisper.right .quote { text-align: right; }
  .whisper .credit {
    margin-top: 8px;
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .whisper.right .credit { text-align: right; }
</style>
<div class="label">30-raat ka hisaab</div>
<div class="main">${formatHours(s.avg_hours)}</div>
<div class="cap">per night, on average — your typical raat ka qissa</div>
<div class="grid">
  <div class="cell"><div class="v">${Math.round(s.avg_quality)}</div><div class="k">Quality / 100</div></div>
  <div class="cell"><div class="v">${s.consistency_score}</div><div class="k">Consistency</div></div>
  <div class="cell"><div class="v">${s.total_nights_logged}</div><div class="k">Nights logged</div></div>
</div>
${best || worst ? `
<div class="whispers">
  ${best ? `
  <aside class="whisper left">
    <div class="glyph">"</div>
    <div class="quote">Sabse achhi raat — ${best.duration_hours.toFixed(1)} ghante neend mili, quality ${best.quality_score}/100.</div>
    <div class="credit">${ddmm(best.date)} · Personal best</div>
  </aside>` : '<div></div>'}
  ${worst ? `
  <aside class="whisper right">
    <div class="glyph">"</div>
    <div class="quote">Sabse buri raat — sirf ${worst.duration_hours.toFixed(1)} ghante, quality ${worst.quality_score}.</div>
    <div class="credit">${ddmm(worst.date)} · Karaz raat</div>
  </aside>` : '<div></div>'}
</div>` : ''}
`;
  }
}

customElements.define('aramgah-hours-spotlight', AramgahHoursSpotlight);
